import {useCallback} from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';
import styled from 'styled-components';
import {MonthlyStoreCount} from '@/map/@types';
import {palette} from '@/styles';
import {CHART_COLORS} from '../constants';
import {chartWithComma, yearMonthTickFormatter} from '../utils';
import {CustomizedLegend, CustomizedTooltip} from './CustomizedChartItem';

export interface IProps {
  type: string;
  data: Array<MonthlyStoreCount & {idx: number}>;
  compareList: string[];
}

export const TotalStoreCountTrendBiaxialLineChart = ({
  type,
  data: rawdata,
  compareList,
}: IProps) => {
  const getChartData = useCallback(() => {
    const dateArray = rawdata[0].monthlyStoreCount.map((v) => {
      return {
        name: v.date,
      };
    });

    const monthlyStoreCountArray = rawdata.map((data) =>
      data.monthlyStoreCount.map((v) => v.count)
    );

    return dateArray.map((month, index) => ({
      ...month,
      ...compareList.reduce((acc: {[key: string]: any}, name, idx) => {
        acc[name] = monthlyStoreCountArray[idx][index];

        return acc;
      }, {}),
    }));
  }, [compareList, rawdata]);

  const chartData = getChartData();

  if (!rawdata) {
    return null;
  }

  return (
    <Root key={type}>
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid
              vertical={false}
              stroke={palette.gray100}
              strokeWidth={1}
            />
            <XAxis
              dataKey="name"
              tickLine={false}
              tick={{fontSize: 12, fill: palette.gray600}}
              tickFormatter={yearMonthTickFormatter}
            />
            <YAxis
              interval={0}
              tick={{fontSize: 12, fill: palette.gray400}}
              axisLine={false}
              tickLine={false}
              tickFormatter={chartWithComma}
            />
            <Tooltip
              content={<CustomizedTooltip />}
              cursor={{
                fill: '#0000000D',
              }}
            />
            <Legend content={<CustomizedLegend marginTop="12px" />} />
            {compareList.map((item, index) => {
              return (
                <Line
                  key={`total-store-count-trend-${index}`}
                  type="monotone"
                  dataKey={item}
                  stroke={CHART_COLORS[index]}
                  dot={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChartWrapper = styled.div`
  height: 157px;
`;
